import { ApiFile } from "@/types/common.types.ts";
import { ApiEmployee } from "@/types/employees/employee.types.ts";
import {
  ApiApplyFormSectionSave,
  ApiJob,
  ApiJobEmployeeConfig,
  ApiJobSavePayload,
  JobStatus,
  JobPriority,
} from "@/types/recruit/job.types.ts";
import { ApiField, ApiSection } from "@/types/custom-field.types.ts";
import { JobEditStep } from "@/pages/recruit/jobs/edit/job-edit.types.ts";
import { createContext, useContext } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

export interface JobEditSystemFormData {
  title: string;
  status: JobStatus;
  priority: JobPriority | null;
  division_id: number | null;
  department_id: number | null;
  job_profile_id: number | null;
  location_id: number | null;
  salary_min: number | null;
  salary_max: number | null;
  currency_code: string | null;
  pipeline_id: number;
  description: string | null;
  emoji: string;
  approval_policy_id: number | null;
  publish_on_career_site: boolean;
  career_site_ids: number[] | null;
  job_archiving_reason_id: number | null;
  use_ai: boolean;
  ai_requirements: string | null;
  requirements: string | null;
  responsibilities: string | null;
  conditions: string | null;
  public_title: string | null;
  career_site_apply_config: ApiApplyFormSectionSave[] | null;
  recruiters_config: ApiJobEmployeeConfig[];
  stakeholders_config: ApiJobEmployeeConfig[];
  files: ApiFile[];
}

export type JobEditFormData = JobEditSystemFormData & Record<string, unknown>;

export type JobFormSection = ApiSection & { fields: ApiField[] };

export type JobEditFormContextType = UseFormReturn<JobEditFormData>;

export const JobEditFormContext = createContext<JobEditFormContextType | null>(null);

export const useJobEditForm = () => {
  const context = useContext(JobEditFormContext);
  if (!context) {
    throw new Error("useJobEditForm must be used within JobEditFormProvider");
  }
  return context;
};

export interface JobEditDataContextType {
  job?: ApiJob;
  jobId: number;
  sections: JobFormSection[];
  getPayload: () => ApiJobSavePayload;

  removeFile: (file: ApiFile) => void;

  recruiters: ApiEmployee[];
  setRecruiters: (recruiters: ApiEmployee[]) => void;

  stakeholders: ApiEmployee[];
  setStakeholders: (stakeholders: ApiEmployee[]) => void;
}

export const JobEditDataContext = createContext<JobEditDataContextType | null>(null);

export const useJobEditData = () => {
  const context = useContext(JobEditDataContext);
  if (!context) {
    throw new Error("useJobEditData must be used within JobEditDataProvider");
  }
  return context;
};

export const useJobEditSteps = (): Record<JobEditStep, string> => {
  const { t } = useTranslation();
  return {
    [JobEditStep.details]: t("common:details"),
    [JobEditStep.career]: t("career_sites:career_page"),
    [JobEditStep.final]: "",
  };
};
